/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.34.0
 * @since 1.24.0
 */

import { SyntheticEvent, useCallback, useMemo, useState } from 'react';

import { TNotificationId, TNotificationsItemData } from '@nexign/scc-ui-kit/lib/components/Notifications/types';
import { useSelector } from 'react-redux';

import { makeSelectAuthoritiesIsServiceUser, makeSelectFeatureEnabled } from '@/main/pages/PageAuth/selectors/authSelectors';
import { useSendNewsItemViewedMutation } from '@/main/pages/PageNews/store';

import { makeSelectUserNotificationsTranslation } from '../../internationalization/intSelectors';
import { FeatureNameEnum } from '../../role/constants/guard/guardConstants';
import {
    ENotificationItemSeverity,
    ENotificationsFeedbackCode,
    ENotificationsItemType,
    ENotificationUpdateStatus,
} from '../constants';
import { useDeleteNotificationMutation, useSendNotificationFeedbackMutation, useUpdateNotificationMutation } from '../store';
import { INotificationsItem, TUseNontificationDataReturnType } from '../types';
import { convertBoolToNotificationStatus } from '../utils/commonNotificationsUtils';
import { getBannerNotificationData } from '../utils/getBannerNotificationData';
import { getCashbackNotificationData } from '../utils/getCashbackNotificationData';
import { getCvmNotificationData } from '../utils/getCvmNotificationData';
import { getEdmNotificationData } from '../utils/getEdmNotificationData';
import { getInquiriesNotificationData } from '../utils/getInquiriesNotificationData';
import { getNewsNotificationData } from '../utils/getNewsNotificationData';
import { getOrdersNotificationData } from '../utils/getOrdersNotificationData';
import { getIsGroupNotification } from '../utils/notificationsUtils';

export const useNotificationsData = (
    items?: INotificationsItem[],
    disableControls?: boolean,
    handleNotificationsClose?: () => void,
): TUseNontificationDataReturnType => {
    const notificationsTranslation = useSelector(makeSelectUserNotificationsTranslation());
    const isServiceUser = useSelector(makeSelectAuthoritiesIsServiceUser());
    const isCvmNotificationsEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.CVM_NOTIFICATIONS));
    const isCashbackNotificationsEnabled =
        useSelector(makeSelectFeatureEnabled(FeatureNameEnum.CASHBACK_NOTIFICATIONS));

    const [currentNotification, setCurrentNotification] = useState<TNotificationId | null>(null);

    const [deleteNotification, { isLoading: deleteNotificationIsLoading }] = useDeleteNotificationMutation();
    const [updateNotification, { isLoading: updateNotificationIsLoading }] = useUpdateNotificationMutation();

    const [sendNewsItemViewed] = useSendNewsItemViewedMutation();
    const [sendNotificationFeedback] = useSendNotificationFeedbackMutation();

    const itemIsLoading = deleteNotificationIsLoading || updateNotificationIsLoading;

    // TODO подумать над концепцией оптимистичного апдейта
    const createViewItemHandler = useCallback(
        (isGroup: boolean, type: ENotificationsItemType) => {
            return (e?: SyntheticEvent, id?: TNotificationId, viewed?: boolean) => {
                e?.stopPropagation();
                if (id) {
                    setCurrentNotification(id);
                    updateNotification({
                        id,
                        isGroup,
                        type,
                        status: convertBoolToNotificationStatus(!viewed),
                    });
                }
            };
        },
        [updateNotification],
    );

    const createDeleteItemHandler = useCallback(
        (isGroup: boolean, type: ENotificationsItemType) => {
            return (e?: SyntheticEvent, id?: TNotificationId) => {
                e?.stopPropagation();
                if (id) {
                    setCurrentNotification(id);
                    deleteNotification({ id, isGroup, type });
                }
            };
        },
        [deleteNotification],
    );

    const handleViewItemButton = useCallback(
        (id: TNotificationId) => {
            return (currentNotification === id && itemIsLoading) || disableControls || isServiceUser;
        },
        [currentNotification, disableControls, itemIsLoading, isServiceUser],
    );
    const handleDeleteItemButton = useCallback(
        (id: TNotificationId) => {
            return (currentNotification === id && itemIsLoading) || disableControls || isServiceUser;
        },
        [currentNotification, disableControls, itemIsLoading, isServiceUser],
    );

    const renderItems: TUseNontificationDataReturnType = useMemo(() => {
        if (!items || !items.length) return [[], { itemIsLoading }];

        const formattedItems: TNotificationsItemData[] = [];

        items.forEach((el) => {
            const isGroup = getIsGroupNotification(el.entity);

            const viewItemHandler = createViewItemHandler(isGroup, el.type);
            const deleteItemHandler = createDeleteItemHandler(isGroup, el.type);

            switch (el.type) {
                case ENotificationsItemType.NOTIFICATION_CVM: {
                    const { id, viewed, entity, type } = el;

                    const handleExternalCvmsNotificationViewed = (id: number | string) => {
                        sendNotificationFeedback({
                            id: id,
                            type: ENotificationsItemType.NOTIFICATION_CVM,
                            feedbackCode: ENotificationsFeedbackCode.TARGET_ACTION_EXECUTED,
                        })
                    };

                    const [content, ItemLink] = getCvmNotificationData(
                        entity,
                        notificationsTranslation,
                        handleExternalCvmsNotificationViewed,
                        isServiceUser,
                        handleNotificationsClose,
                    );

                    if (content && isCvmNotificationsEnabled)
                        formattedItems.push({
                            id,
                            key: `${type}_${id}`,
                            severity: ENotificationItemSeverity.CVM,
                            viewed,
                            content,
                            ItemLink,
                            onDeleteItemClick: deleteItemHandler,
                            disableDeleteButton: handleDeleteItemButton(id),
                        });

                    break;
                }
                case ENotificationsItemType.NOTIFICATION_BANNER: {
                    const { viewed, entity } = el;

                    const [content, ItemLink] = getBannerNotificationData(
                        entity,
                        notificationsTranslation,
                        handleNotificationsClose,
                    );
                    if (content)
                        formattedItems.push({
                            id: String(entity.type),
                            key: `${entity.type}`,
                            severity: ENotificationItemSeverity.DANGER,
                            viewed,
                            content,
                            ItemLink,
                        });

                    break;
                }
                case ENotificationsItemType.NOTIFICATION_ORDERS: {
                    const { id, viewed, entity, type } = el;

                    const [content, ItemLink] = getOrdersNotificationData(
                        entity,
                        isGroup,
                        notificationsTranslation,
                        handleNotificationsClose,
                    );

                    formattedItems.push({
                        id,
                        key: `${type}_${id}`,
                        severity: ENotificationItemSeverity.SUCCESS,
                        viewed,
                        content,
                        ItemLink,
                        onViewItemClick: viewItemHandler,
                        onDeleteItemClick: deleteItemHandler,
                        disableViewButton: handleViewItemButton(id),
                        disableDeleteButton: handleDeleteItemButton(id),
                    });
                    break;
                }
                case ENotificationsItemType.NOTIFICATION_INQUIRIES: {
                    const { id, viewed, entity, type } = el;

                    const [content, ItemLink] = getInquiriesNotificationData(
                        entity,
                        isGroup,
                        notificationsTranslation,
                        handleNotificationsClose,
                    );

                    formattedItems.push({
                        id,
                        key: `${type}_${id}`,
                        severity: ENotificationItemSeverity.WARNING,
                        viewed,
                        content,
                        ItemLink,
                        onViewItemClick: viewItemHandler,
                        onDeleteItemClick: deleteItemHandler,
                        disableViewButton: handleViewItemButton(id),
                        disableDeleteButton: handleDeleteItemButton(id),
                    });
                    break;
                }
                case ENotificationsItemType.NOTIFICATION_EDM: {
                    const { id, viewed, entity, type } = el;

                    const [content, ItemLink] = getEdmNotificationData(
                        entity,
                        notificationsTranslation,
                        handleNotificationsClose,
                    );
                    if (content) {
                        formattedItems.push({
                            id,
                            key: `${type}_${id}`,
                            severity: ENotificationItemSeverity.WARNING,
                            viewed,
                            content,
                            ItemLink,
                            onViewItemClick: viewItemHandler,
                            onDeleteItemClick: deleteItemHandler,
                            disableViewButton: handleViewItemButton(id),
                            disableDeleteButton: handleDeleteItemButton(id),
                        });
                    }

                    break;
                }
                case ENotificationsItemType.NOTIFICATION_NEWS: {
                    const { id, viewed, entity, type } = el;

                    const handleExternalNewsNotificationViewed = (id: string | number) => {
                        sendNewsItemViewed({ id });
                    };

                    const [content, ItemLink] = getNewsNotificationData(
                        entity,
                        notificationsTranslation,
                        handleExternalNewsNotificationViewed,
                        handleNotificationsClose,
                    );

                    formattedItems.push({
                        id,
                        key: `${type}_${id}`,
                        severity: ENotificationItemSeverity.WARNING,
                        viewed,
                        content,
                        ItemLink,
                        onViewItemClick: viewItemHandler,
                        onDeleteItemClick: deleteItemHandler,
                        disableViewButton: handleViewItemButton(id),
                        disableDeleteButton: handleDeleteItemButton(id),
                    });
                    break;
                }
                case ENotificationsItemType.NOTIFICATION_CASHBACK: {
                    const { id, viewed, entity, type } = el;

                    const handleCashbackNotificationViewed = (id: string | number) => {
                        updateNotification({
                            id: id,
                            type: ENotificationsItemType.NOTIFICATION_CASHBACK,
                            status: ENotificationUpdateStatus.VIEWED,
                        });
                    };

                    const [content, ItemLink]  = getCashbackNotificationData(
                        id,
                        entity,
                        notificationsTranslation,
                        handleCashbackNotificationViewed,
                        handleNotificationsClose);

                    if (content && isCashbackNotificationsEnabled)
                        formattedItems.push({
                            id,
                            key: `${type}_${id}`,
                            severity: ENotificationItemSeverity.WARNING,
                            viewed,
                            content,
                            ItemLink,
                            onViewItemClick: viewItemHandler,
                            onDeleteItemClick: deleteItemHandler,
                            disableViewButton: handleViewItemButton(id),
                            disableDeleteButton: handleDeleteItemButton(id),
                        });
                    break;
                }
                default:
                    break;
            }
        });
        return [formattedItems, { itemIsLoading }];
    }, [
        isCvmNotificationsEnabled,
        isCashbackNotificationsEnabled,
        isServiceUser,
        items,
        itemIsLoading,
        createViewItemHandler,
        createDeleteItemHandler,
        notificationsTranslation,
        handleNotificationsClose,
        handleViewItemButton,
        handleDeleteItemButton,
        updateNotification,
        sendNewsItemViewed,
        sendNotificationFeedback,
    ]);

    return renderItems;
};
